import { useExamEventDetails } from '@exam-center/exam-event-management';
import {
  GetExamEventByIdDocument,
  GetExamEventExamineesForMonitoringDocument,
  useEcExtendExamEventSessionExamineesTimeMutation,
  useExtendExamEventSessionTimeMutation,
  useGetExamEventParticipatedCoursesQuery,
} from '@exam-center/graphql';
import { useServerClock } from '@exam-center/server-time';
import { formatStartToEndTime } from '@exam-center/utils';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  ErrorState,
  Flex,
  Form,
  FormButton,
  FormNumberInput,
  FormRadio,
  FormTextarea,
  RadioGroup,
  Text,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { getAdjustedSessionTime, shouldHideSessionTimeExtension } from '../../utils';
import { ExamCourseTimeExtensionList } from '../exam-course-time-extension-list';
import { ExamineeTimeExtensionList } from '../examinee-time-extension-list';

import ExtendTimeConfirmationModal from './extend-time-confirmation-modal';
import { EXTEND_TYPE, ExtendTimeFormSchema, type ExtendTimeFormType } from './schema';

interface ExtendTimeModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
}

export default function ExtendTimeModal(props: Readonly<ExtendTimeModalProps>) {
  const { isOpen, onClose, sessionId } = props;
  const { examEvent } = useExamEventDetails();
  const { showSnackbar } = useSnackbar();
  const [isConfirmationOpen, { open: openConfirmationModal, close: closeConfirmationModal }] =
    useDisclosure();
  const now = useServerClock();

  const { data: examEventParticipatedCoursesData } = useGetExamEventParticipatedCoursesQuery({
    variables: {
      input: {
        examEventId: examEvent.id as string,
      },
    },
    skip: !isOpen || !examEvent,
    fetchPolicy: 'cache-first',
  });

  const participatedCourses =
    examEventParticipatedCoursesData?.ecGetExamEventParticipatedCourses.examEventCourses ?? [];

  const currentSession = examEvent.sessions.find((session) => session.id === sessionId);

  const adjustedSessionTime = useMemo(
    () =>
      currentSession
        ? getAdjustedSessionTime(currentSession, currentSession.postponeDurationInMs ?? 0)
        : null,
    [currentSession],
  );
  const examStartDate = adjustedSessionTime?.startedAt ?? new Date();
  const examEndDate = adjustedSessionTime?.endedAt ?? new Date();

  const formContext = useForm<ExtendTimeFormType>({
    defaultValues: {
      extendType: EXTEND_TYPE.SESSION,
      amount: undefined,
      examineeIds: [],
      remark: '',
    },
    resolver: zodResolver(ExtendTimeFormSchema),
  });

  const extendType = formContext.watch('extendType');

  const handleClose = () => {
    formContext.reset();
    closeConfirmationModal();
    onClose();
  };

  const onSubmit = async () => {
    openConfirmationModal();
  };

  const [extendExamEventSessionTime, { loading: isExtendExamEventSessionTimeLoading }] =
    useExtendExamEventSessionTimeMutation({
      onCompleted: () => {
        showSnackbar({
          message: 'ทดเวลาสอบเรียบร้อยแล้ว',
          variant: 'success',
        });
        handleClose();
      },
      onError: () => {
        showSnackbar({
          message: 'เกิดข้อผิดพลาดในการทดเวลาสอบ',
          variant: 'error',
        });
      },
      awaitRefetchQueries: true,
      refetchQueries: [GetExamEventByIdDocument, GetExamEventExamineesForMonitoringDocument],
    });

  const [
    extendExamEventSessionExamineesTime,
    { loading: isExtendExamEventSessionExamineesTimeLoading },
  ] = useEcExtendExamEventSessionExamineesTimeMutation({
    onCompleted: () => {
      showSnackbar({
        message: 'ทดเวลาสอบเรียบร้อยแล้ว',
        variant: 'success',
      });
      handleClose();
    },
    onError: () => {
      showSnackbar({
        message: 'เกิดข้อผิดพลาดในการทดเวลาสอบ',
        variant: 'error',
      });
    },
    awaitRefetchQueries: true,
    refetchQueries: [GetExamEventByIdDocument, GetExamEventExamineesForMonitoringDocument],
  });

  const handleConfirm = async () => {
    const values = formContext.getValues();

    switch (values.extendType) {
      case EXTEND_TYPE.SESSION:
        return await extendExamEventSessionTime({
          variables: {
            extendExamEventSessionTimeInput: {
              examEventId: examEvent.id,
              sessionId: currentSession?.id ?? '',
              extendedTime: values.amount,
              remark: values.remark,
            },
          },
        });
      case EXTEND_TYPE.EXAMINEE:
        return await extendExamEventSessionExamineesTime({
          variables: {
            extendExamEventSessionExamineesTimeInput: {
              examEventId: examEvent.id,
              sessionId: currentSession?.id ?? '',
              examEventSessionExamineeIds: values.examineeIds ?? [],
              extendedTimeInMinutes: values.amount,
              remark: values.remark,
            },
          },
        });
    }
  };

  const isSessionEnd =
    currentSession &&
    shouldHideSessionTimeExtension(
      participatedCourses,
      currentSession,
      currentSession.postponeDurationInMs ?? 0,
      now,
    );

  const shouldDisableSubmitButton =
    (extendType === EXTEND_TYPE.EXAMINEE && !formContext.watch('examineeIds')?.length) ||
    isSessionEnd;

  return (
    <>
      <FormProvider {...formContext}>
        <Dialog
          open={isOpen}
          maxWidth='xl'
          slotProps={{
            paper: {
              component: Form,
              onSubmit: formContext.handleSubmit(onSubmit),
            },
          }}
        >
          <DialogTitle onClose={handleClose}>ทดเวลาสอบ</DialogTitle>
          <Divider />
          <DialogContent>
            {isSessionEnd ? (
              <Alert severity='error' sx={(theme) => ({ mb: theme.spacing(6) })}>
                <Text>รอบสอบสิ้นสุดแล้ว ไม่สามารถทดเวลาได้</Text>
              </Alert>
            ) : null}
            {currentSession ? (
              <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(6) })}>
                <Box
                  sx={(theme) => ({
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    borderRadius: theme.shape.rounded(2),
                    p: theme.spacing(4),
                    backgroundColor: theme.palette.grey[50],
                  })}
                >
                  <Text>
                    ช่วงสอบ:{' '}
                    <Text component='span' variant='body1Semibold'>
                      {formatStartToEndTime(examStartDate, examEndDate)}
                    </Text>
                  </Text>
                </Box>

                <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(4) })}>
                  <Text variant='h4'>เลือกรูปแบบการทดเวลา</Text>
                  <Flex
                    sx={(theme) => ({
                      flexDirection: 'column',
                      gap: theme.spacing(4),
                      p: theme.spacing(4),
                      border: '1px solid',
                      borderColor: theme.palette.divider,
                      borderRadius: theme.shape.rounded(2),
                      backgroundColor: theme.palette.grey[50],
                    })}
                  >
                    <Text variant='body1Semibold'>รูปแบบการทดเวลา</Text>
                    <RadioGroup row>
                      <FormRadio
                        name='extendType'
                        value={EXTEND_TYPE.SESSION}
                        label='ทดเวลาทั้งรอบ'
                      />
                      <FormRadio
                        name='extendType'
                        value={EXTEND_TYPE.EXAMINEE}
                        label='ทดเวลารายคน'
                      />
                    </RadioGroup>
                  </Flex>
                </Flex>

                <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(4) })}>
                  {extendType === EXTEND_TYPE.SESSION ? (
                    <>
                      <Text variant='h4'>ข้อมูลหลักสูตร</Text>
                      <ExamCourseTimeExtensionList
                        examEventId={examEvent.id}
                        examStartDate={examStartDate}
                        currentSession={currentSession}
                      />
                    </>
                  ) : null}
                  <Box
                    hidden={extendType !== EXTEND_TYPE.EXAMINEE}
                    aria-hidden={extendType !== EXTEND_TYPE.EXAMINEE}
                  >
                    <ExamineeTimeExtensionList
                      currentSessionId={currentSession.id}
                      name='examineeIds'
                      examEventId={examEvent.id}
                      now={now}
                    />
                  </Box>
                </Flex>
                <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(4) })}>
                  <Text variant='h4'>ระบุจำนวนเวลาที่ต้องการทด</Text>
                  <Box sx={{ width: '318px' }}>
                    <FormNumberInput
                      name='amount'
                      label='จำนวนเวลา (นาที)'
                      placeholder='ระบุจำนวนเวลา'
                      helperText='ผู้เข้าสอบแต่ละคนจะได้รับเวลาไม่เกินเวลาที่ทดได้ของตนเอง'
                      required
                    />
                  </Box>
                </Flex>

                <FormTextarea
                  name='remark'
                  label='เหตุผลในการทดเวลา'
                  placeholder='กรุณาระบุเหตุผล'
                  required
                />
              </Flex>
            ) : (
              <ErrorState />
            )}
          </DialogContent>
          <Divider />
          <DialogActions>
            <Button size='large' variant='outlined' color='secondary' onClick={handleClose}>
              ยกเลิก
            </Button>
            <FormButton label='ทดเวลาสอบ' size='large' disabled={shouldDisableSubmitButton} />
          </DialogActions>
        </Dialog>
      </FormProvider>
      <ExtendTimeConfirmationModal
        open={isConfirmationOpen}
        values={formContext.getValues()}
        onConfirm={handleConfirm}
        onClose={closeConfirmationModal}
        loading={
          isExtendExamEventSessionTimeLoading || isExtendExamEventSessionExamineesTimeLoading
        }
      />
    </>
  );
}
